/**
 * The sign-in flow's requests, made before a session exists or while one is being chosen. A
 * request carries the Google sign-in as a Bearer token, or the session cookie when it is null.
 * A 401 answers the question being asked, so none of them sends the page to the login screen; a
 * request on the session cookie refreshes it once first.
 */

import { z } from 'zod';

import type { MeResponse, SessionResponse } from '@/types/auth';
import { LocalLoginResponseSchema, SessionResponseSchema } from '@/types/auth';
import { ViolationSourceKind } from '@/types/contract';
import type { Server } from '@/types/servers';
import { ServerSchema } from '@/types/servers';

import { createBoundary } from './client';
import { fetchWithCookie } from './http';
import { fetchWithCookieRefresh } from './session';
import { MeResponseSchema } from './transformers/auth';

function getCredentialInit(credential: string | null): RequestInit {
    return credential ? { headers: { Authorization: `Bearer ${credential}` } } : {};
}

const API = '/vms/api/v1';
const ServerNameSchema = ServerSchema.pick({
    id: true,
    name: true,
});
const SESSION_URL = `${API}/auth/session`;
const { fetchParsed, postParsed } = createBoundary(
    { kind: ViolationSourceKind.Cloud }, fetchWithCookie,
);
const session = createBoundary({ kind: ViolationSourceKind.Cloud }, fetchWithCookieRefresh);

/** Who the caller is, and every organization they belong to or are invited to. */
export function fetchMe(credential: string | null): Promise<MeResponse> {
    return fetchParsed(`${API}/me`, MeResponseSchema, getCredentialInit(credential));
}

/** Creates an organization; the answer carries its session. */
export function postSetup(credential: string | null, orgName: string): Promise<MeResponse> {
    return postParsed(
        `${API}/setup`, { orgName }, MeResponseSchema, getCredentialInit(credential),
    );
}

/** Joins an organization the caller was invited to; the answer carries its session. */
export function postAcceptInvite(credential: string | null, orgId: string): Promise<MeResponse> {
    return postParsed(
        `${API}/invite/accept`, { orgId }, MeResponseSchema, getCredentialInit(credential),
    );
}

export async function postDeclineInvite(credential: string | null, orgId: string): Promise<void> {
    await postParsed(
        `${API}/invite/decline`, { orgId }, z.unknown(), getCredentialInit(credential),
    );
}

/**
 * A session in one organization, set as the cookie and returned for the in-tab copy. The
 * credential is the Google sign-in, or the session cookie when null: page JS never holds it.
 */
export function postSession(credential: string | null, orgId: string): Promise<SessionResponse> {
    if (credential) {
        return postParsed(SESSION_URL, { token: credential, orgId }, SessionResponseSchema);
    }
    return session.postParsed(SESSION_URL, { orgId }, SessionResponseSchema);
}

/** The token of the session for the local-login popup: it opens a media_server only. */
export async function postLocalLoginToken(): Promise<string> {
    const { token } = await session.postParsed(
        `${API}/auth/local-login-token`, {}, LocalLoginResponseSchema,
    );
    return token;
}

/** Ends each session of the user, on each device, this one included. */
export async function postRevokeAllSessions(): Promise<void> {
    await session.postParsed(`${API}/auth/sessions/revoke-all`, {}, z.unknown());
}

export function fetchServerName(
    serverId: string, orgId: string,
): Promise<Pick<Server, 'id' | 'name'>> {
    const query = new URLSearchParams({ orgId });
    return session.fetchParsed(
        `${API}/servers/${encodeURIComponent(serverId)}?${query}`, ServerNameSchema,
    );
}
